import { ICONS } from "./icons/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import * as pdfMake from "pdfmake/build/pdfmake";
import * as pdfFonts from "pdfmake/build/vfs_fonts";
import { PdfPreviewProps } from "./types/types";
import pieChartBase64 from "../charts/pieChart";
import lineChartBase64 from "../charts/lineChart/intex";
import * as JSON5 from "json5";
import { Skeleton, Typography } from "@mui/material";
import { evalCode, evalView } from "./utils/evalCode";
import * as dayjs from "dayjs";
import * as utc from "dayjs/plugin/utc";
import * as timezone from "dayjs/plugin/timezone";
import { useOutletContext } from "react-router-dom";

dayjs.extend(utc);
dayjs.extend(timezone);

(pdfMake as any).vfs = (pdfFonts as any).default || pdfFonts;
interface OutletContextType {
  config: EasyReportConfig[];
  setConfig: React.Dispatch<React.SetStateAction<EasyReportConfig[]>>;
}

export const PdfPreview: React.FC<PdfPreviewProps> = ({
  variableNames,
  code,
  rawContent,
  styles,
  verifyContent,
  setPdfError,
  watermark = false,
}) => {
  const [headerImg, setHeaderImg] = useState(new Map());
  const [content, setContent] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string>("");

  const { config } = useOutletContext<OutletContextType>();
  const headerConfig = useMemo(() => {
    const result = config?.find((c) => c.nome === "header");
    if (result) {
      return result.configuracoes;
    }
    return {};
  }, [config]);

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const generateChartBase64 = async (item: any) => {
    let base64 = "";
    if (item.image === "pieChart" && item.dataset) {
      base64 = await pieChartBase64(item.dataset, item.labels);
    }
    if (item.image === "lineChart" && item.dataset) {
      base64 = await lineChartBase64(
        item.dataset,
        item.labels,
        item.percentil,
        item.mask,
        item.line,
        item.isTraffic,
        item.options,
      );
    }
    return base64;
  };

  const processContent = async (content: string) => {
    if (!content) {
      return;
    }
    try {
      const parsed = JSON5.parse(content);
      const processItem = async (item: any): Promise<any> => {
        if (
          (item.image === "pieChart" || item.image === "lineChart") &&
          item.dataset
        ) {
          const base64 = await generateChartBase64(item);
          return {
            ...item,
            image: base64,
            fit: item.fit || [500, 150],
            alignment: item.alignment || "center",
          };
        }
        if (
          item.image &&
          item.image !== "pieChart" &&
          item.image !== "lineChart"
        ) {
          setError(
            `O valor '${item.image}' não é permitido. Apenas 'pieChart' e 'lineChart' são permitidos.`,
          );
          if (setPdfError) setPdfError(true);
          return null;
        }
        if (item.ul || item.ol || item.text) {
          return { ...item, unbreakable: true };
        }
        if (item.stack) {
          return {
            ...item,
            stack: await Promise.all(item.stack.map(processItem)),
            unbreakable: true,
          };
        }
        if (item.columns) {
          return {
            ...item,
            columns: await Promise.all(item.columns.map(processItem)),
          };
        }
        if (item.table) {
          return {
            style: "tableExample",
            table: {
              ...item.table,
              headerRows: item.table.headerRows ?? 1,
              widths:
                item.table.widths || Array(item.table.body[0].length).fill("*"),
              body: item.table.body.map((row: any, rowIndex: number) =>
                row.map((cell: any) =>
                  rowIndex === 0
                    ? {
                        text:
                          typeof cell === "string" ? cell : cell.text || cell,
                        style: "tableHeader",
                        bold: true,
                      }
                    : {
                        text:
                          typeof cell === "string" ? cell : cell.text || cell,
                        style: "tableCell",
                      },
                ),
              ),
            },
            layout: {
              fillColor: function (rowIndex: number) {
                if (rowIndex === 0) return "#42474b";
                return rowIndex % 2 === 0 ? "#e4e4e4" : "#ffffff";
              },
              hLineWidth: function (i: number, node: any) {
                if (i === 0) return 0;
                if (i === 1) return 2;
                if (i === node.table.body.length) return 1.5;
                return 0;
              },
              vLineWidth: function () {
                return 0;
              },
              hLineColor: function (i: number) {
                return i === 1 ? "#17222c" : "#e0e0e0";
              },
              paddingLeft: function (i: number) {
                return i === 0 ? 15 : 12;
              },
              paddingRight: function (i: number) {
                return i === 0 ? 15 : 12;
              },
              paddingTop: function (i: number) {
                return i === 0 ? 12 : 8;
              },
              paddingBottom: function (i: number) {
                return i === 0 ? 12 : 8;
              },
            },
          };
        }
        return item;
      };
      const processed = await Promise.all(parsed.map(processItem));
      const filtered = processed.filter((item: any) => item !== null);
      setContent(filtered);
    } catch (error) {}
  };

  const docDefinition = () => {
    const images: Record<string, string> = {};
    const contentCopy = content;

    const getImages = (items: any[]): void => {
      items.forEach((item: any) => {
        if (
          item.image &&
          typeof item.image === "string" &&
          item.image.startsWith("data:image")
        ) {
          const imageKey = `img_${Object.keys(images).length}`;
          images[imageKey] = item.image;
          item.image = imageKey;
        }
        // Recursivamente busca imagens em stacks, columns, etc.
        if (item.stack) getImages(item.stack);
        if (item.columns) getImages(item.columns);
        if (item.table && item.table.body) {
          item.table.body.forEach((row: any) => getImages(row));
        }
      });
    };

    return {
      info: {
        title: "D3 Charts Report",
        author: "Your Company",
      },
      pageSize: "A4" as "A4",
      pageMargins: [40, 80, 40, 60] as [number, number, number, number],
      header: function () {
        const contactInfo: any[] = [];
        if (headerConfig.email)
          contactInfo.push({
            columns: [
              { text: "", width: "*" },
              { image: ICONS.email, width: 12 },
              {
                text: headerConfig.email,
                style: "headerContact",
                width: "auto",
              },
            ],
            columnGap: 5,
          });
        if (headerConfig.phone)
          contactInfo.push({
            columns: [
              { text: "", width: "*" },
              { image: ICONS.phone, width: 12 },
              {
                text: headerConfig.phone,
                style: "headerContact",
                width: "auto",
              },
            ],
            columnGap: 5,
          });
        if (headerConfig.instagram)
          contactInfo.push({
            columns: [
              { text: "", width: "*" },
              { image: ICONS.instagram, width: 12 },
              {
                text: headerConfig.instagram,
                style: "headerContact",
                width: "auto",
              },
            ],
            columnGap: 5,
          });

        const columns: any[] = [];
        if (headerImg.get(headerConfig.imgLink)) {
          columns.push({
            image: headerImg.get(headerConfig.imgLink),
            fit: [150, 45],
            margin: [0, 0, 10, 0],
            alignment: "left",
          });
        }
        if (contactInfo.length > 0) {
          columns.push({
            stack: contactInfo,
            width: "*",
          });
        }
        return {
          stack: [
            {
              columns: columns,
              margin: [40, 30, 40, 5],
            },

            {
              canvas: [
                {
                  type: "line",
                  x1: 40,
                  y1: 0,
                  x2: 555,
                  y2: 0,
                  lineWidth: 0.1,
                  lineColor: "#7d7979",
                },
              ],
            },
          ],
        } as any;
      },
      footer: function (currentPage: number, pageCount: number) {
        return {
          stack: [
            {
              canvas: [
                {
                  type: "line",
                  x1: 40,
                  y1: 0,
                  x2: 555,
                  y2: 0,
                  lineWidth: 0.5,
                  lineColor: "#bcbcbc",
                },
              ],
              margin: [0, 0, 0, 10],
            },
            {
              columns: [
                {
                  text: `Made by EasyReport - ${new Date().toLocaleDateString()}`,
                  style: "small",
                },
                {
                  text: currentPage,
                  style: "small",
                  alignment: "right",
                },
              ],
              margin: [40, 0, 40, 20],
            },
          ],
        } as any;
      },
      pageBreakBefore: function (
        currentNode: any,
        followingNodesOnPage: any[],
      ): boolean {
        return (
          currentNode.headlineLevel === 1 && followingNodesOnPage.length === 0
        );
      },
      content: contentCopy as any,
      watermark: watermark
        ? {
            text: "EXEMPLO",
            color: "#ff7300",
            opacity: 0.3,
            fontSize: 120,
            bold: true,
            italics: false,
          }
        : undefined,
      images: images,
    };
  };

  const validateContent = (items: any[]) => {
    const isValid = (item: any, path: string = ""): boolean => {
      if (!item || typeof item !== "object") return false;
      const validKeys = [
        "image",
        "text",
        "stack",
        "columns",
        "table",
        "ul",
        "ol",
        "width",
        "height",
        "alignment",
        "style",
        "margin",
        "pageBreak",
        "unbreakable",
        "dataset",
        "labels",
        "percentil",
        "line",
        "layout",
        "fit",
        "mask",
        "type",
        "isTraffic",
        "options",
      ];
      const itemKeys = Object.keys(item);
      const invalidKey = itemKeys.find((key) => !validKeys.includes(key));

      if (invalidKey) {
        setError(
          `Estrutura do documento inválida. Chave inválida '${invalidKey}'`,
        );
        if (setPdfError) setPdfError(true);
        return false;
      }

      if (
        item.stack &&
        !item.stack.every((child: any, idx: number) =>
          isValid(child, `${path}.stack[${idx}]`),
        )
      )
        return false;
      if (
        item.columns &&
        !item.columns.every((child: any, idx: number) =>
          isValid(child, `${path}.columns[${idx}]`),
        )
      )
        return false;

      return true;
    };

    if (!items.every((item, idx) => isValid(item, `content[${idx}]`))) {
      return false;
    }
    return true;
  };

  useEffect(() => {
    let evaluatedContent = [];
    try {
      if (code.some((c) => c && c.trim().length > 0)) {
        for (let i = 0; i < code.length; i++) {
          const codeFormated = evalCode(code[i], variableNames[i], setError);
          if (!codeFormated) {
            if (setPdfError) setPdfError(true);
            return;
          }
          evaluatedContent.push(
            evalView(codeFormated, rawContent[i], dayjs, setError, i),
          );
        }
      } else {
        rawContent.length > 0
          ? setError("Necessario um código para o preview")
          : setError("");
        if (setPdfError) setPdfError(true);
        return;
      }
    } catch (err) {
      setError("Erro ao avaliar o código: " + (err as Error).message);
      if (setPdfError) setPdfError(true);
      evaluatedContent = [];
    }

    const formattedContent = evaluatedContent.reduce((acc, item) => {
      try {
        const parsed = JSON5.parse(item);
        acc.push(...parsed);
      } catch {}
      return acc;
    }, []);

    processContent(JSON5.stringify(formattedContent, null, 2));
  }, [headerConfig, rawContent, code, variableNames, styles]);

  useEffect(() => {
    const fetchImages = async () => {
      try {
        if (headerConfig.imgLink && !headerImg.get(headerConfig.imgLink)) {
          setHeaderImg((prev) => {
            const newMap = new Map(prev);
            newMap.set(headerConfig.imgLink, headerConfig.imgSrc || null);
            return newMap;
          });
        }
      } catch (error) {
        console.error("Error fetching header image:", error);
      }
    };
    fetchImages();
  }, [headerConfig.imgLink]);

  useEffect(() => {
    if (verifyContent && rawContent.length === 0) {
      setError("Selecione um perfil para gerar o PDF.");
      if (setPdfError) setPdfError(true);
      setPdfUrl("");
      return;
    }

    if (content.length === 0) {
      return;
    }

    setLoading(true);
    setError(null);
    if (setPdfError) setPdfError(false);

    try {
      const mergedStyles = Object.assign({}, ...styles);
      const doc = docDefinition();

      if (!validateContent(doc.content.flat())) {
        return;
      }

      const pdfDoc = pdfMake.createPdf({
        ...docDefinition(),
        styles: {
          headerContact: {
            fontSize: 9,
            color: "#7f8c8d",
          },
          tableExample: {
            margin: [0, 10, 0, 20],
            fontSize: 10,
          },
          tableHeader: {
            fontSize: 11,
            bold: true,
            color: "#ffffff",
            fillColor: "#42474b",
          },
          tableCell: {
            fontSize: 10,
            color: "#42474b",
            lineHeight: 1.4,
          },
          ...mergedStyles,
        },
      });

      pdfDoc.getDataUrl((dataUrl: string) => {
        setPdfUrl(dataUrl);
      });
    } catch (err: any) {
      console.error("Error generating PDF:", err);
      setError(
        err?.message ||
          "Erro ao gerar o PDF. Verifique a estrutura do documento.",
      );
      if (setPdfError) setPdfError(true);
      setLoading(false);
    }
  }, [headerImg, config, content, styles]);

  useEffect(() => {
    if (pdfUrl && iframeRef.current) {
      iframeRef.current.src = pdfUrl;
    }
  }, [pdfUrl]);

  const getToolbarHeight = () => {
    const ua = navigator.userAgent;
    const isMobile = /Mobile|Android|iPhone|iPad/i.test(ua);
    if (ua.includes("Firefox")) {
      return isMobile ? 56 : 32;
    }
    if (ua.includes("Chrome")) return 56;
    if (ua.includes("Edg")) return 56;
    if (
      ua.includes("Safari") &&
      !ua.includes("Chrome") &&
      !ua.includes("Edg")
    ) {
      return isMobile ? 32 : 40;
    }
    return 40;
  };

  if (error) {
    return (
      <Typography
        style={{
          padding: "10px",
          wordBreak: "break-word",
          whiteSpace: "pre-wrap",
          overflow: "hidden",
          maxWidth: "100%",
        }}
      >
        {error}
      </Typography>
    );
  }

  return (
    <>
      {loading && <Skeleton variant="rectangular" width="100%" height="100%" />}
      <iframe
        ref={iframeRef}
        title="PDF Preview"
        style={{
          width: "100%",
          height: `calc(100% + ${getToolbarHeight()}px)`,
          marginTop: `-${getToolbarHeight()}px`,
          border: "none",
          display: loading ? "none" : "block",
        }}
        onLoad={() => {
          if (pdfUrl) {
            setLoading(false);
          }
        }}
      />
    </>
  );
};
